import Tooltip from '@mui/material/Tooltip';
import {
  type GridApiRef,
  type GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import { memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeliveriesTestIds } from '~/constants/test-ids';

import { useQueryUserData } from '~/data/user';

import ExportService from '~/services/export.service';
import ToastService from '~/services/toast.service';

import { LOG, log } from '~/utils/logging';
import {
  usePersistedUserSettings,
  USER_SETTINGS_KEY,
} from '~/utils/usePersistedUserSettings';

import {
  DatagridToolbarCustomComponent,
  type ToolbarIconKey,
} from '~/ui/molecules/Datagrid';

export type CustomToolbarButton = {
  alignRight?: boolean;
  className?: string;
  disabled?: boolean;
  tooltip?: string;
  onClickShowOptions?: () => void;
  icon?: ToolbarIconKey;
  menuItems: Array<{
    isLoading?: boolean;
    name: string;
    onClick: () => void;
    selected?: boolean;
    styleOverrides?: Record<string, any>;
  }>;
  testId?: string;
  title: string;
};

type P = {
  readonly apiRef: GridApiRef;
  readonly customPdfExportButton?: CustomToolbarButton; // if the button is supplied, then it is in charge of running the export itself and the onPdfExport is not used
  readonly excelColumns?: GridColDef[];
  readonly excelData?: any[];
  readonly multiplePdfDownload?: boolean;
  readonly noColumnsButton?: boolean;
  readonly noExportButton?: boolean;
  readonly noFilterButton?: boolean;
  readonly hasRowSelection?: boolean;
  readonly onApproveDeliveryNotes?: () => void;
  readonly onBillDeliveryNotes?: () => void;
  readonly onDeclineDeliveryNotes?: () => void;
  readonly onExportCSVFromBackend?: (visibleColumns: string[]) => void;
  readonly onExportExcelFromBackend?: (visibleColumns: string[]) => void;
  readonly onMapDirectDeliveryNote?: () => void;
  readonly onMultiPermissionGrantEdit?: () => void;
  readonly onPdfExport?: (
    downloadOption?: string,
    doctype?: 'docx' | 'original' | 'original_enhanced',
    includeAttachments?: boolean,
  ) => void;
  readonly onRequestDeliveryNoteSignature?: () => void;
  readonly onShareDeliveryNote?: () => void;
  readonly productAnalyticsFeature?: string;
  readonly rowsCount: number;
};

export const GridToolbar = memo(
  ({
    apiRef,
    customPdfExportButton,
    excelColumns,
    excelData,
    multiplePdfDownload,
    noColumnsButton,
    noExportButton,
    noFilterButton,
    hasRowSelection,
    onApproveDeliveryNotes,
    onBillDeliveryNotes,
    onDeclineDeliveryNotes,
    onExportCSVFromBackend,
    onExportExcelFromBackend,
    onMapDirectDeliveryNote,
    onMultiPermissionGrantEdit,
    onPdfExport,
    onRequestDeliveryNoteSignature,
    onShareDeliveryNote,
    productAnalyticsFeature,
    rowsCount,
  }: P) => {
    const { t } = useTranslation('delivery');

    const { data: currentUser } = useQueryUserData(true);
    const userPermissions = currentUser?.userPermissions ?? [];

    const hasApprovalPermission =
      userPermissions.includes('dln_approval.write');
    const hasBillingPermission = userPermissions.includes('dln_billing.write');

    const hasRows = Boolean(rowsCount);

    const [internalExportEntityType, setInternalExportEntityType] = useState<
      string | undefined
    >(undefined);

    const { getUserSettings, persistUserSettings } = usePersistedUserSettings({
      settingsKey: USER_SETTINGS_KEY.DATA_EXPORT_SETTINGS,
    });

    const savedExportSettings = getUserSettings() as {
      preferredFormat?: 'csv' | 'excel';
    };

    const lastExportSelection = savedExportSettings?.preferredFormat ?? null;

    const handleExportFromBackend = useCallback(
      (format: 'excel' | 'csv' = 'excel') => {
        const visibleColumns = apiRef.current
          .getVisibleColumns()
          .map(({ field }: { field: string }) => field);

        if (format === 'excel') {
          onExportExcelFromBackend?.(visibleColumns);
        } else {
          onExportCSVFromBackend?.(visibleColumns);
        }
      },
      [apiRef, onExportCSVFromBackend, onExportExcelFromBackend],
    );

    const handleExportExcel = useCallback(
      (withProductAnalyticsEvent: boolean) => {
        if (withProductAnalyticsEvent) {
          log.productAnalyticsEvent(
            'Download Excel',
            productAnalyticsFeature ?? LOG.FEATURE.EXCEL_DOWNLOAD,
          );
        }

        if (excelData.length === 0) {
          ToastService.info([
            'Bitte wähle für den Excel-Download mindestens einen Eintrag aus der Tabelle aus.',
          ]);

          log.productAnalyticsEvent(
            'No row selected in Excel-Download',
            productAnalyticsFeature ?? LOG.FEATURE.EXCEL_DOWNLOAD,
            LOG.TYPE.FAILED_VALIDATION,
          );

          return;
        }

        const rows = [excelColumns.map(({ headerName }) => headerName)];

        rows.push(
          ...excelData.map((data) =>
            excelColumns.map(({ field }) => data[field]),
          ),
        );

        const entityType =
          internalExportEntityType ?? ExportService.ENTITIES.DELIVERIES;

        ExportService.exportExcel(rows, {
          customRange: undefined,
          entityType,
          predefinedRange: undefined,
        });
      },
      [
        excelColumns,
        excelData,
        internalExportEntityType,
        productAnalyticsFeature,
      ],
    );

    const customComponents = useMemo(() => {
      const components: CustomToolbarButton[] = [];
      const actionButtons: CustomToolbarButton[] = [];

      // Collect export menu items for combined export dropdown
      const exportMenuItems: Array<{
        format: 'csv' | 'excel';
        name: string;
        onClick: () => void;
        isLoading?: boolean;
        selected?: boolean;
        styleOverrides?: Record<string, any>;
      }> = [];

      if (onExportCSVFromBackend) {
        exportMenuItems.push({
          format: 'csv',
          name: t(($) => $.toolbar.export.csv.title, {
            ns: 'delivery',
          }),
          onClick() {
            persistUserSettings({ preferredFormat: 'csv' });
            handleExportFromBackend('csv');
          },
        });
      }

      if ((excelData && excelColumns) ?? onExportExcelFromBackend) {
        exportMenuItems.push({
          format: 'excel',
          name: t(($) => $.toolbar.export.excel.title, {
            ns: 'delivery',
          }),
          onClick() {
            persistUserSettings({ preferredFormat: 'excel' });
            if (onExportExcelFromBackend) {
              handleExportFromBackend('excel');
            } else {
              handleExportExcel(true);
            }
          },
        });
      }

      if (exportMenuItems.length > 0) {
        const primaryItem = exportMenuItems.find(
          ({ format }) => format === lastExportSelection,
        );
        const orderedExportMenuItems = primaryItem
          ? [
              primaryItem,
              ...exportMenuItems.filter(
                ({ format }) => format !== lastExportSelection,
              ),
            ]
          : exportMenuItems;
        components.push({
          icon: 'excel',
          menuItems: orderedExportMenuItems,
          title: 'Exportieren',
        });
      }

      // Use custom PDF export button if provided, otherwise use default
      if (customPdfExportButton) {
        components.push(customPdfExportButton);
      } else if (onPdfExport) {
        // Simple default PDF export (original behavior before modal was added)
        const pdfExportMenuItems = multiplePdfDownload
          ? [
              {
                name: t(($) => $.pdfDownload.button.mainTitle),
                onClick() {
                  onPdfExport(ExportService.DOWNLOAD_OPTION.MERGE);
                },
              },
            ]
          : [
              {
                name: t(($) => $.pdfDownload.button.mainTitle),
                onClick() {
                  onPdfExport();
                },
              },
            ];

        components.push({
          icon: 'pdf',
          menuItems: pdfExportMenuItems,
          title: t(($) => $.pdfDownload.button.mainTitle),
        });
      }

      if (onMultiPermissionGrantEdit) {
        components.push({
          icon: 'permissionGrant',
          menuItems: [
            {
              name: 'Berechtigungen vergeben',
              onClick: onMultiPermissionGrantEdit,
            },
          ],
          title: 'Berechtigungen vergeben',
        });
      }

      if (onRequestDeliveryNoteSignature) {
        components.push({
          icon: 'requestSignature',
          menuItems: [
            {
              name: 'Signaturen anfordern',
              onClick: onRequestDeliveryNoteSignature,
            },
          ],
          testId: DeliveriesTestIds.SIGNATURE_FORM_BUTTON,
          title: 'Signaturen anfordern',
        });
      }

      if (onShareDeliveryNote) {
        components.push({
          icon: 'shareDeliveryNote',
          menuItems: [
            {
              name: 'Lieferungen teilen',
              onClick: onShareDeliveryNote,
            },
          ],
          title: 'Lieferungen teilen',
        });
      }

      if (onMapDirectDeliveryNote) {
        components.push({
          icon: 'mapDirectDeliveryNote',
          menuItems: [
            {
              name: 'Standort zuweisen',
              onClick: onMapDirectDeliveryNote,
            },
          ],
          testId: DeliveriesTestIds.ASSIGN_SITE_FORM_BUTTON,
          title: 'Standort zuweisen',
        });
      }

      if (hasApprovalPermission && onApproveDeliveryNotes) {
        actionButtons.push({
          className:
            'bg-blue-200 text-blue-600 hover:bg-blue-300 disabled:bg-gray-100 disabled:text-gray-400',
          disabled: !hasRowSelection || !hasApprovalPermission,
          icon: 'approve',
          menuItems: [
            {
              name: 'Freigeben',
              onClick: onApproveDeliveryNotes,
            },
          ],
          title: 'Freigeben',
          tooltip: hasRowSelection
            ? t(($) => $.toolbar.actions.approve.tooltip)
            : t(($) => $.shared.table.selectRowRequired, { ns: 'common' }),
        });
      }

      if (hasApprovalPermission && onDeclineDeliveryNotes) {
        actionButtons.push({
          className:
            'bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
          disabled: !hasRowSelection || !hasApprovalPermission,
          icon: 'decline',
          menuItems: [
            {
              name: 'Nicht freigeben',
              onClick: onDeclineDeliveryNotes,
            },
          ],
          title: 'Nicht freigeben',
          tooltip: hasRowSelection
            ? t(($) => $.toolbar.actions.decline.tooltip)
            : t(($) => $.shared.table.selectRowRequired, { ns: 'common' }),
        });
      }

      if (hasBillingPermission && onBillDeliveryNotes) {
        actionButtons.push({
          className:
            'bg-green-200 text-green-800 hover:bg-green-300 disabled:bg-gray-100 disabled:text-gray-400',
          disabled: !hasRowSelection || !hasBillingPermission,
          icon: 'bill',
          menuItems: [
            {
              name: 'Abrechnen',
              onClick: onBillDeliveryNotes,
            },
          ],
          title: 'Abrechnen',
          tooltip: hasRowSelection
            ? t(($) => $.toolbar.actions.bill.tooltip)
            : t(($) => $.shared.table.selectRowRequired, { ns: 'common' }),
        });
      }

      if (actionButtons.length > 0) {
        const [firstAction, ...restActions] = actionButtons;
        components.push({ ...firstAction!, alignRight: true }, ...restActions);
      }

      return components;
    }, [
      customPdfExportButton,
      excelColumns,
      excelData,
      handleExportExcel,
      handleExportFromBackend,
      hasApprovalPermission,
      hasBillingPermission,
      lastExportSelection,
      multiplePdfDownload,
      hasRowSelection,
      onApproveDeliveryNotes,
      onBillDeliveryNotes,
      onDeclineDeliveryNotes,
      onExportCSVFromBackend,
      onExportExcelFromBackend,
      onMapDirectDeliveryNote,
      onMultiPermissionGrantEdit,
      onPdfExport,
      onRequestDeliveryNoteSignature,
      onShareDeliveryNote,
      persistUserSettings,
      t,
    ]);

    useLayoutEffect(() => {
      setInternalExportEntityType(
        apiRef?.current?.state?.customMetadata?.internalExportEntityType,
      );
    }, [
      apiRef,
      apiRef?.current?.state?.customMetadata?.internalExportEntityType,
    ]);

    const csvOptionsMemoized = useMemo(() => {
      return {
        fileName: ExportService.generateFileName({
          entityType:
            internalExportEntityType ?? ExportService.ENTITIES.DELIVERIES,
          format: '',
        }),
      };
    }, [internalExportEntityType]);

    return (
      <GridToolbarContainer className="flex gap-2">
        {noColumnsButton ? null : <GridToolbarColumnsButton />}
        {noFilterButton ? null : <GridToolbarFilterButton />}
        {noExportButton || !hasRows ? null : (
          <GridToolbarExport
            csvOptions={csvOptionsMemoized}
            printOptions={{ disableToolbarButton: true }}
          />
        )}
        {customComponents.map(
          ({
            alignRight,
            className,
            disabled,
            tooltip,
            onClickShowOptions,
            icon,
            menuItems,
            testId,
            title,
          }) => (
            <div key={title} className={alignRight ? 'ml-auto' : undefined}>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <span
                    className={`inline-flex ${
                      (disabled ?? !hasRows) ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    <DatagridToolbarCustomComponent
                      buttonProps={{
                        'data-testid': testId,
                        disabled: disabled ?? !hasRows,
                      }}
                      className={className}
                      icon={icon}
                      menuItems={menuItems}
                      title={title}
                      onClickShowOptions={onClickShowOptions}
                    />
                  </span>
                </Tooltip>
              ) : (
                <DatagridToolbarCustomComponent
                  buttonProps={{
                    'data-testid': testId,
                    disabled: disabled ?? !hasRows,
                  }}
                  className={className}
                  icon={icon}
                  menuItems={menuItems}
                  title={title}
                  onClickShowOptions={onClickShowOptions}
                />
              )}
            </div>
          ),
        )}
      </GridToolbarContainer>
    );
  },
);

GridToolbar.displayName = 'GridToolbar';
